import type { TitleSize } from '@tempo-io/roadmap-dto';
import { cn } from '@tempo-io/sui-class-utils';
import { differenceInDays, isWithinInterval } from 'date-fns';
import { type Dispatch, memo, type SetStateAction, useMemo } from 'react';
import { ITEM_LANE_HEIGHT, ITEM_LANE_HEIGHT_COMPACT, TimelineItemContainer } from './TimelineItem';
import type { TimelineItemType, TimelineRowType } from './useTimelineTableColumns';
import { lightenTimelineBgColor } from './utils/timelineColorUtils';
import { calculateLanesByOrder } from './utils/timelineStackingUtils';

const generateLaneLines = ({
  totalLanes,
  isRowDraggedOver,
  rowColor,
  laneHeight,
}: {
  totalLanes: number;
  isRowDraggedOver: boolean;
  rowColor: string;
  laneHeight: number;
}) => {
  return Array.from({ length: totalLanes }, (_, i) => i).map((lane) => (
    <div
      key={`lane-line-${lane}`}
      className="absolute w-full border-t-4 border-border-neutral-subtle pointer-events-none"
      style={{
        top: `${lane * laneHeight + laneHeight / 2}px`,
        borderColor: isRowDraggedOver ? lightenTimelineBgColor(rowColor, 50) : undefined,
      }}
    />
  ));
};

const calculateItemOffset = (
  item: TimelineItemType,
  allItemsInRow: TimelineItemType[],
  expandedChildren: { [key: number]: boolean },
  laneHeight: number,
): number => {
  const currentOrder = item.order || 0;
  const lanesByOrder = calculateLanesByOrder(allItemsInRow, expandedChildren);

  let offset = 0;
  for (const [order, lanes] of lanesByOrder.entries()) {
    if (order >= currentOrder) {
      break;
    }
    offset += lanes * laneHeight;
  }
  return offset;
};

export const TimelineItemCell = memo(
  ({
    itemsForInterval,
    intervalStart,
    intervalEnd,
    showTodayLine,
    from,
    to,
    canResize,
    canDragBetweenHeaders,
    canDragDates,
    rowColor,
    itemColorById,
    dayWidth,
    titleSize,
    isRowDraggedOver,
    isDropForbidden,
    expandedChildren,
    showChildItems,
    setExpandedChildren,
    onResizeEnd,
    onItemClick,
    row,
    isCompact,
    allItemsInRow,
    headerLeftOffset,
    extraLanes,
    progressFieldId,
    systemProgressMap,
    hideName = false,
    isPublishedMode = false,
  }: {
    itemsForInterval: TimelineItemType[];
    intervalStart: Date;
    intervalEnd: Date;
    showTodayLine: boolean;
    from: string;
    to: string;
    canResize: boolean;
    canDragBetweenHeaders: boolean;
    canDragDates: boolean;
    rowColor: string;
    itemColorById?: Record<number, string>;
    dayWidth: number;
    titleSize: TitleSize;
    showChildItems: boolean;
    isRowDraggedOver: boolean;
    isDropForbidden: boolean;
    expandedChildren: Record<string, boolean>;
    setExpandedChildren: Dispatch<
      SetStateAction<{
        [key: number]: boolean;
      }>
    >;
    onResizeEnd?: (itemId: number, newStartDate: string, newEndDate: string) => void;
    onItemClick: (itemId: number) => void;
    row?: TimelineRowType;
    isCompact?: boolean;
    allItemsInRow: TimelineItemType[];
    headerLeftOffset: number;
    extraLanes?: number;
    progressFieldId: number | null;
    systemProgressMap: Map<number, number> | null;
    hideName?: boolean;
    isPublishedMode?: boolean;
  }) => {
    const today = new Date();
    const laneHeight = isCompact ? ITEM_LANE_HEIGHT_COMPACT : ITEM_LANE_HEIGHT;

    const totalLanes = useMemo(() => {
      const lanesByOrder = calculateLanesByOrder(allItemsInRow, expandedChildren);
      const baseLanes = Array.from(lanesByOrder.values()).reduce((sum, lanes) => sum + lanes, 0);
      return Math.max(baseLanes, 1) + (extraLanes ?? 0);
    }, [allItemsInRow, expandedChildren, extraLanes]);

    const requiredHeight = Math.max(totalLanes * laneHeight, laneHeight);
    const laneLines = useMemo(
      () =>
        !isCompact
          ? generateLaneLines({ totalLanes, isRowDraggedOver, rowColor, laneHeight })
          : null,
      [totalLanes, isRowDraggedOver, rowColor, laneHeight, isCompact],
    );

    const shouldRenderTodayLine =
      showTodayLine && isWithinInterval(today, { start: intervalStart, end: intervalEnd });

    return (
      <div
        className={cn('relative w-full', {
          'bg-bg-neutral-subtle': isCompact && !isRowDraggedOver && !isDropForbidden,
        })}
        style={{
          height: requiredHeight,
          cursor: isDropForbidden ? 'not-allowed' : undefined,
          ...(isCompact && isRowDraggedOver
            ? { backgroundColor: lightenTimelineBgColor(rowColor, 30) }
            : {}),
        }}
      >
        {laneLines}
        <div
          className="relative w-full h-full"
          style={{
            left: headerLeftOffset,
          }}
        >
          {shouldRenderTodayLine && (
            <div
              className="absolute top-0 bottom-0 border-l border-border-error-subtle pointer-events-none z-1"
              style={{
                left: `${differenceInDays(today, intervalStart) * dayWidth}px`,
              }}
            />
          )}

          {itemsForInterval.map((item) => {
            const containerTopOffset = calculateItemOffset(
              item,
              allItemsInRow,
              expandedChildren,
              laneHeight,
            );

            return (
              <TimelineItemContainer
                isCompact={isCompact ?? false}
                canResize={canResize}
                canDragBetweenHeaders={canDragBetweenHeaders}
                canDragDates={canDragDates}
                timelineStartDate={from}
                timelineEndDate={to}
                key={item.id}
                item={item}
                color={itemColorById?.[item.id] ?? rowColor}
                dayWidth={dayWidth}
                titleSize={titleSize}
                intervalStart={intervalStart}
                isExpanded={(showChildItems && expandedChildren[item.id]) ?? false}
                setExpandedChildren={setExpandedChildren}
                onResizeEnd={onResizeEnd}
                onItemClick={onItemClick}
                row={row}
                itemColorById={itemColorById}
                containerTopOffset={containerTopOffset}
                progressFieldId={progressFieldId}
                systemProgressMap={systemProgressMap}
                hideName={hideName}
                isPublishedMode={isPublishedMode}
              />
            );
          })}
        </div>
      </div>
    );
  },
);

TimelineItemCell.displayName = 'TimelineItemCell';
